import { AccountTeamUserState } from './account-team-user.reducer';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  CallStateInviteUser,
  CallStateLoadUsers,
  CallStateRemoveUser,
  CallStateUpdateUserRole,
} from './account-team-user.enum';
import { getHighestRoleForOrganization } from './helpers/get-highest-role';
import { sortBy } from 'lodash-es';
import { getOrganizationId } from '../user/user.selectors';
import { OrganizationInvitationStatus } from 'src/app/admin/models/organization.model';
import { OrganizationUser } from './account-team-user.model';

const getAccountTeamUserState = createFeatureSelector<AccountTeamUserState>('accountTeamUser');

export const getAccountTeamUsers = createSelector(
  getOrganizationId,
  getAccountTeamUserState,
  (organizationId, state) =>
    state.organizationUsers.map((user) => ({
      ...user,
      organizationRoles: getHighestRoleForOrganization(organizationId, user),
    })),
);

const sortUsersByName = (users: OrganizationUser[]) =>
  sortBy(users, (user) => user.name.toLowerCase());

const checkInvitationStatus = (
  user: OrganizationUser,
  organizationId: number,
  inviteStatus: OrganizationInvitationStatus,
  legacyCheck: boolean,
) => {
  const orgInvitation = user.user_metadata?.invitations?.[organizationId];
  return orgInvitation ? orgInvitation.status === inviteStatus : legacyCheck;
};

export const getActiveAccountTeamUsers = createSelector(
  getAccountTeamUsers,
  getOrganizationId,
  (users, organizationId) =>
    users.filter((user) =>
      checkInvitationStatus(
        user,
        organizationId,
        OrganizationInvitationStatus.Accepted,
        user.email_verified,
      ),
    ),
);

export const getActiveAccountTeamUsersSortedByName = createSelector(
  getActiveAccountTeamUsers,
  sortUsersByName,
);

export const getPendingAccountTeamUsers = createSelector(
  getAccountTeamUsers,
  getOrganizationId,
  (users, organizationId) =>
    users.filter((user) =>
      checkInvitationStatus(
        user,
        organizationId,
        OrganizationInvitationStatus.Sent,
        !user.email_verified,
      ),
    ),
);

export const getPendingAccountTeamUsersSortedByName = createSelector(
  getPendingAccountTeamUsers,
  sortUsersByName,
);

export const getCallStateLoadUsersError = createSelector(
  getAccountTeamUserState,
  (state) => state.callStateLoadUsers === CallStateLoadUsers.ERROR && state.apiError,
);

export const isCallStateInviteUserSuccess = createSelector(
  getAccountTeamUserState,
  (state) => state.callStateInviteUser === CallStateInviteUser.SUCCESS,
);

export const isCallStateInviteUserInProgress = createSelector(
  getAccountTeamUserState,
  (state) => state.callStateInviteUser === CallStateInviteUser.IN_PROGRESS,
);

export const getAccountTeamUserRequestError = createSelector(
  getAccountTeamUserState,
  (state) => state.apiError,
);

export const isCallStateRemoveUserSuccess = createSelector(
  getAccountTeamUserState,
  (state) => state.callStateRemoveUser === CallStateRemoveUser.SUCCESS,
);

export const isCallStateRemoveUserPending = createSelector(
  getAccountTeamUserState,
  (state) => state.callStateRemoveUser === CallStateRemoveUser.PENDING,
);

export const isCallStateUpdateUserRoleSuccess = createSelector(
  getAccountTeamUserState,
  (state) => state.callStateUpdateUserRole === CallStateUpdateUserRole.SUCCESS,
);

export const selectUserInEdit = createSelector(
  getAccountTeamUserState,
  getOrganizationId,
  ({ userInEdit }, organizationId) =>
    userInEdit
      ? {
          ...userInEdit,
          organizationRoles: getHighestRoleForOrganization(organizationId, userInEdit),
        }
      : null,
);
